import { verifyLuhnsCheckDigit } from "./luhnAlgorithmUtil";

/**
* Returns whether the order is flagged as a government payor (Medicare/Tricare) order.
* An order is considered a government order if governmentPayor = true or if any requisition has the Medicare
* billing method selected.
*
* @param gearOrder the Gear Order to test
*/
export const isGovernmentPayorOrder = (gearOrder) => {
   return (
	   gearOrder &&
	   (gearOrder.governmentPayor === true ||
		   (gearOrder.requisitions &&
			   gearOrder.requisitions.some(
				   (req) => req.billingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE
			   )))
   );
}

/**
* Returns a display name for the insurance provider key
*/
export const getInsuranceProviderDisplayName = (provider) => {
   if (!provider) {
	   return '';
   }
   switch (provider) {
	   case 'aetna': return "Aetna / Aetna Medicare Advantage";
	   case 'humana': return "Humana / Humana Gold / Humana Medicare Advantage";
	   case 'kaiser': return "Kaiser / Kaiser Medicare Advantage";
	   case 'principal': return "Principal";
	   case 'tufts': return "Tufts / Tufts Medicare Advantage";
	   case 'no-coverage': return "No health insurance coverage";
	   case 'cigna': return "Cigna";
	   case 'united-healthcare': return "United Healthcare";
	   case 'other': return "My insurance is not listed / All other companies";
	   case 'medicare': return "Medicare (not Medicare Advantage)";
	   case 'bcbs-fep': return "Blue Cross Blue Shield Federal Employees Program (FEP)";
	   case 'bcbs': return "Blue Cross Blue Shield (BCBS) / BCBS Medicare Advantage";
	   case 'medicaid': return "Medicaid / Medicaid Managed Care Plans";
	   case 'tricare': return "TriCare";
	   case 'champus': return "CHAMPUS";
	   case 'medicare_railroad': return "Railroad Retirement Board";
	   case 'novalue': 
	   default: return "Unknown Carrier";
   }
}

/**
 * Returns a customized display name for billing types
 */
export const getBillingTypeDisplayName = (billingType) => {
	if (!billingType) {
		return '';
	}

	// If the type is a UK Patient Pay sub-option, get the display name for the main option
	if (billingType.startsWith(process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE + ':')) {
		let parts = billingType.split(':');		
		return getBillingTypeDisplayName(parts[0]);		
	}

	switch (billingType) {
		case process.env.REACT_APP_BILLING_INSURANCE_VALUE :
		case process.env.REACT_APP_BILLING_ONLY_INSURANCE_VALUE : return 'Bill Insurance';
		case process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE: return 'Patient Pay';
		case process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE : return 'Bill Practitioner';
		case process.env.REACT_APP_BILLING_PAYMENT_ENCLOSED_VALUE : return 'Bill Patient';
		default: return billingType;
	}
}

/*
 * Removes any non-digits from the requisition number
 */
const cleanRequisitionNumber = (reqNum) => {
	return reqNum.replace(/\D/g, '');
};

/**
 * Removes any non-alphanumeric characters from the input and converts it to upper case
 */
export const cleanActivationCode = (input) => {
	return input?.replace(/[^0-9a-z]/gi, '').toUpperCase();
}

/**
 * Verify the code starts with 'C' and is followed by 9 digits and that the numeric 
 * portion of the code has a valid Luhn check digit.
 */
export const isValidActivationCode = (input) => {
	const rawCode = cleanActivationCode(input);
	return /^C\d{9}$/.test(rawCode) && verifyLuhnsCheckDigit(rawCode);
}

/**
 * Finds a Gear requisition object in a Gear order by oms req id or activation code.
 *
 * @param gearOrder The gearOrder to search
 * @param searchString the oms requisition id or activation code to search by
 */
export const getRequisitionFromOrderByReqNumberOrActivationCode = ( gearOrder, searchString ) => {
	let activeReq = null;
	if (gearOrder?.requisitions?.length && searchString) {
		activeReq = gearOrder.requisitions.find((item) => {
			// Remove non-digits from searchString and compare to item.reqNumber or gear order id
			// Remove special characters and compare to item.activationCode
			return item.reqNumber === +cleanRequisitionNumber(searchString)
				|| cleanActivationCode(item.activationCode) === cleanActivationCode(searchString)
				|| gearOrder.id === +cleanRequisitionNumber(searchString);
		});

		/* Searching by CSS ReqFormID won't find a match because we return the mapped activation code.
		 * Return the first requisition if the search string is 9 digits (ignoring special characters).
		 */
		if (!activeReq && /^\d{9}$/.test(cleanActivationCode(searchString))) {
			activeReq = gearOrder.requisitions[0];
		}

		/* 
		 * SCR-7173: A requisition's activation code may not exactly match the input activation code
		 *  if the activation card is one of the initial batch that mixed up the middle 3 digits. If 
		 *  the active req could not be found with a direct match, attempt to match by ignoring the middle 
		 *  three digits. In other words, match the first 4 characters (Cxxx) and the last 3 digits.
		 * 
		 * Example: C403524176 -> C403241176 | match C403***176
		 */
		if (!activeReq) {
			activeReq = gearOrder.requisitions.find((item) => {
				if (item.activationCode) {
					const cleanInput = cleanActivationCode(searchString);
					return (
						cleanInput.slice(0, 4) === item.activationCode.slice(0, 4) && // Match first 4 characters
						cleanInput.slice(-3) === item.activationCode.slice(-3) // Match last 3 characters
					);
				}
				return false;
			});
		}
	}
	return activeReq;
}

/**
 * Check if the practitioner on this order uses a domestic requisition layout type.
 * 
 * @gearOrder The gearOrder to check
 * @return true if the practitioner's office uses domestic requisitions (or if the layout type cannot be determined), false otherwise
 */
export const isDomesticRequisitionType = (gearOrder) => {
	const office = gearOrder?.practitioner?.office;
	return !office?.reqLayoutId || office?.reqLayoutId === 0 || office?.reqLayoutId === 2;
}

/**
 * Check if the practitioner on this order is located in Canada.
 * 
 * @gearOrder The gearOrder to check
 * @return true if the practitioner's office is in Canada, false otherwise
 */
export const isCanadianClient = (gearOrder) => {
	return gearOrder?.practitioner?.office?.address?.country?.toUpperCase() === 'CANADA';
}

/**
 * Check if the order contains drop-ship or ship-to-office requisitions that are being shipped to Canada.
 * 
 * @gearOrder The gearOrder to check
 * @return true if the shipping destination is Canada
 */
export const isShippingToCanada = (gearOrder) => {
	return (
		(gearOrder?.containsDropShipRequisition || gearOrder?.containsShipToOfficeRequisition) &&
		(gearOrder.patientInfo?.addresses?.find((addr) => addr.type === 'shipping')?.country === 'Canada' ||
			gearOrder.shippingInformation?.country === 'Canada')
	);
};

/**
 * Get the order's country.
 * An order's country is determined by the patient's shipping address country, if available. Otherwise, 
 * the practitioner's office country is used.
 *
 * @gearOrder
 * @return the order's country name
 */
export const getOrderCountry = ({ gearOrder }) => {
	let orderCountry = gearOrder?.patientInfo?.addresses?.find((addr) => addr.type === 'shipping')?.country;
	if (!orderCountry) {
		orderCountry = gearOrder?.practitioner?.office?.address?.country;
	}

	return orderCountry;
}


/**
 * Check if the req type and shipping address are international
 *
 * @gearOrder The gearOrder to check
 * @return true if the order is international
 */
export const isInternationalOrder = (gearOrder) => {
	return !isDomesticRequisitionType(gearOrder) || (gearOrder.patientInfo?.addresses?.find((addr) => addr.type === 'shipping')?.country !== 'United States')
}

/**
 * Checks if the client on the order is located in Florida or New York
 * 
 * @gearOrder The gearOrder to check
 * @return true if the practitioner's office is in Florida or New York
 */
export const isClientInFlOrNy = (gearOrder) => {
	const clientState = gearOrder?.practitioner?.office?.address?.state?.toUpperCase();
	return clientState === 'NY' || clientState === 'FL';
}

/**
 * Determines if the user can authorize the order. Authorization is allowed if:
 *  - The user has access "On Behalf Of" the order's authorizing doctor (That includes the logged in user being the authorizing doctor).
 */
export const canAuthorizeOrder = (userInfo, gearOrder) => {
	if (userInfo?.type !== 'myGDX' || !gearOrder?.practitioner?.authorizingDoctorGsdlNo) {
		return false;
	}

	const authorizingDoctorGsdlNo = gearOrder.practitioner.authorizingDoctorGsdlNo;
	const onBehalfOf = userInfo.onBehalfOf?.split(',') ?? [];
	return userInfo.gsdlNo === authorizingDoctorGsdlNo || onBehalfOf.includes(authorizingDoctorGsdlNo);
};

/**
 * Provides the same logic as `canAuthorizeOrder` but starts with an OrderHistoryEntry instead of a GearOrder object.
 * The two objects have simliar content but different structures. Convert the OrderHistoryEntry 
 * into the necessary parts of a GearOrder so we can reuse the canAuthorizeOrder method.
 */
export const canAuthorizeHistoryOrder = (userInfo, orderHistoryEntry) => {
	const gearOrder = {
		practitioner: {
			authorizingDoctorGsdlNo: orderHistoryEntry?.authorizingPractitionerGsdlNo
		}
	}
	return canAuthorizeOrder(userInfo, gearOrder);
}

/**
 * Returns true if the order has a recollectionFor value or if every requisition on the order has an xComId
 */
export const isResubmittalOrder = (gearOrder) => {
	return gearOrder?.recollectionFor || gearOrder?.requisitions?.every((req) => req.xComId);
}


/**
 * Returns true if the Gear order contains one or more requisitions and every requisition has an accession number.
 */
export const isFullyAccessioned = (gearOrder) => {
	return gearOrder?.requisitions?.every((req) => req.accessionNo);
}

/**
 * Returns true if the Gear order contains one or more requisitions and any requisition has an accession number.
 */
export const isAnyAccessioned = (gearOrder) => {
	return gearOrder?.requisitions?.some((req) => req.accessionNo);
}

export const isUKOrder = (gearOrder) => {
	return gearOrder?.practitioner?.ukclient;
};

export const currencyMap = {
  US: { symbol: '$', code: 'USD', locale: 'en-US' },
  GB: { symbol: '£', code: 'GBP', locale: 'en-GB' },
 // EU: { symbol: '€', code: 'EUR', locale: 'en-EU' }  
};

export const formatCurrency = (amount, ukOrder) => {
	const currencyInfo = currencyMap[ukOrder ? 'GB' : 'US']; 
	const formattedAmount = parseFloat(Math.round((amount ?? 0) * 100) / 100).toFixed(2);
	return new Intl.NumberFormat(currencyInfo.locale, {
    style: 'currency',
    currency: currencyInfo.code,
  }).format(formattedAmount);

}

export const getInsuranceProviderDisplayNameUK = (provider) => {
   if (!provider) {
	   return '';
   }
   switch (provider) {
	   case 'allianz': return "Allianz";
	   case 'aviva': return "Aviva";
	   case 'axa-ppp': return "Axa-PPP";
	   case 'bupa': return "BUPA";
	   case 'bupa-intl': return "BUPA International";
	   case 'cigna': return "Cigna";
	   case 'healix': return "Healix";
	   case 'wpa': return "WPA";	   
	   case 'other': return "Other"
	   case 'novalue': 
	   default: return "Unknown Carrier";
   }
}

export const getCustomerCareNumber = (gearOrder) => {
	return isUKOrder(gearOrder) ? process.env.REACT_APP_UK_CUSTOMER_CARE_NUMBER : process.env.REACT_APP_CUSTOMER_CARE_NUMBER;
}

/**
 * Checks if the given country is part of the UK or its allowed territories for drop-ship clients.
 */
export const isCountryUk = (country) => {
    return country && ['Great Britain and Northern Ireland', 'United Kingdom', 'Jersey', 'Guernsey', 'Isle of Man'].includes(country);
}

export const isShipToUkGearOrder = (gearOrder) => {
	return isCountryUk(
		gearOrder?.patientInfo?.addresses?.find(addr => addr.type === 'shipping')?.country
	);
};

/**
 * Tests that the UK postal code is in a valid format.
 *  //Allowing 6 or 7 alphanumerics. Can use optional strict pattern as:  /^(GIR\s?0AA|[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})$/;
 * @return true if the postal code format is valid, false otherwise
 */
export const getValidFormattedUKPostalCode = (zipCode) => {  
  if(!zipCode) {
    return null;
  }
  zipCode = zipCode.replace(/\s+/g, ''); // Remove any spaces for validation
  const ukPostcodePattern = /^[A-Za-z0-9]{5,7}$/;
   if(ukPostcodePattern.test(zipCode.trim())) {
	 //Format postal code with a space before the last 3 characters
	 zipCode = zipCode.slice(0, -3) + ' ' + zipCode.slice(-3);
	 return zipCode.toUpperCase();
   } else {
	 return null;
   }
}

export const getLocale = () => {
	const language = navigator?.language?.toLowerCase();
	return (language === 'en' || language === 'en-us' || language === 'en-ca') ? 'en-US' : 'en-GB';
}